import "react-cmdk/dist/cmdk.css";

import { useAnalytics } from "@codat/analytics";
import {
  IndicatorLabel,
  OrchardProvider,
  SearchIcon,
  TextInput,
} from "@codat/orchard-ui";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useEffect, useState } from "react";
import CommandPalette, {
  filterItems,
  getItemIndex,
  useHandleOpenCommandPalette,
} from "react-cmdk";
import { useHistory } from "react-router-dom";

import { createNavPermissionMap } from "components/NavContainer/utils";

import styles from "./CommandSearch.module.scss";
import { getCommandSearchListItems } from "./getCommandSearchListItems";

export const CommandSearch: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const { sendEvent } = useAnalytics();
  const history = useHistory();

  useHandleOpenCommandPalette(setOpen);

  useEffect(() => {
    if (open) {
      sendEvent("Command Bar - Open");
    }
  }, [open, sendEvent]);

  const { userState } = useUserState();

  const navPermissionMap = createNavPermissionMap(
    userState ? userState.userPermissions : [],
    userState?.userRole
  );

  const sendLinkClickEvent = (name: string) => {
    sendEvent("Command Bar - Link - Click", {
      link: name,
    });
  };

  const searchItems = getCommandSearchListItems({
    navPermissionMap,
    navigate: (url: string) => history.push(url),
  });

  const filteredItems = filterItems(
    searchItems.map((x) => ({
      id: x.id,
      heading: x.label,
      items: x.items.map((y) => ({
        id: y.id,
        children: y.label,
        showType: false,
        icon: y.icon,
        iconType: "outline",
        onClick: () => {
          sendLinkClickEvent(y.id);
          y.onClick();
        },
        keywords: y.keywords,
      })),
    })),
    search
  );

  return (
    <>
      <TextInput
        id="cmdk-search"
        label="command-k search"
        showLabel={false}
        disabled
        iconLeft={{ icon: <SearchIcon /> }}
        iconRight={{
          icon: (
            <span className={styles.searchInputIndicators}>
              {navigator?.platform?.toLowerCase().includes("mac") ? (
                <IndicatorLabel color="white" label="⌘ Cmd" />
              ) : (
                <IndicatorLabel color="white" label="Ctrl" />
              )}
              <IndicatorLabel color="white" label="K" />
            </span>
          ),
        }}
        placeholder="Search..."
        onControlClick={() => setOpen(true)}
        className={styles.searchBox}
        data-testid="cmdk-search"
      />

      <CommandPalette
        onChangeSearch={setSearch}
        onChangeOpen={setOpen}
        search={search}
        isOpen={open}
        page="root"
        placeholder="Search..."
      >
        <OrchardProvider appId="search-command-palette">
          <CommandPalette.Page id="root">
            {filteredItems.length ? (
              filteredItems.map((list) => (
                <CommandPalette.List key={list.id} heading={list.heading}>
                  {list.items.map(({ id, ...rest }) => (
                    <React.Fragment key={id}>
                      <CommandPalette.ListItem
                        index={getItemIndex(filteredItems, id)}
                        data-testid="command-search-list-item"
                        {...rest}
                      />
                    </React.Fragment>
                  ))}
                </CommandPalette.List>
              ))
            ) : (
              <CommandPalette.FreeSearchAction />
            )}
          </CommandPalette.Page>
        </OrchardProvider>
      </CommandPalette>
    </>
  );
};
